import React, { useEffect, useRef, useState } from 'react';

const FadeInAnimation = ({
  children,
  id,
  className = '',
  animate = true,
  as: Component = 'div', // Defaults to 'div', but can be 'section', 'article', etc.
  ...props
}) => {
  const storageKey = id ? `fade-in-seen:${id}` : null;
  const [isVisible, setIsVisible] = useState(() => {
    if (!animate || !storageKey || typeof window === 'undefined') return true;

    return window.sessionStorage.getItem(storageKey) === 'true';
  });
  const domRef = useRef();

  useEffect(() => {
    if (!animate) return;

    const el = domRef.current;
    if (!el) return;

    if (isVisible) return;

    // 1. Instant reveal if loaded/refreshed within view
    const rect = el.getBoundingClientRect();
    if (rect.top < window.innerHeight) {
      setIsVisible(true);
      if (storageKey) {
        window.sessionStorage.setItem(storageKey, 'true');
      }
      return;
    }

    // 2. Observer for scrolling into view
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          if (storageKey) {
            window.sessionStorage.setItem(storageKey, 'true');
          }
          observer.unobserve(el);
        }
      },
      { threshold: 0.1 }
    );

    observer.observe(el);

    return () => observer.disconnect();
  }, [animate, isVisible, storageKey]);

  const animationClasses = animate
    ? `transform transition-all duration-[1200ms] ease-out ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
      }`
    : '';

  return (
    <Component
      ref={domRef}
      id={id}
      className={`${animationClasses} ${className}`.trim()}
      {...props}
    >
      {children}
    </Component>
  );
};

export default FadeInAnimation;